export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  /** Nom du token de couleur StyleSystem, ex : "primary-500". */
  token: string;
}

interface DonutChartProps {
  slices: DonutSlice[];
  centerValue: string;
  centerLabel: string;
  formatter: (value: number) => string;
  emptyLabel?: string;
}

/**
 * Camembert (anneau) construit avec un dégradé conique.
 * Les couleurs proviennent uniquement des tokens de la charte graphique.
 */
export default function DonutChart({
  slices,
  centerValue,
  centerLabel,
  formatter,
  emptyLabel = "Aucune donnée à afficher.",
}: DonutChartProps) {
  const visible = slices.filter((slice) => slice.value > 0);
  const total = visible.reduce((sum, slice) => sum + slice.value, 0);

  let cursor = 0;
  const stops = visible
    .map((slice) => {
      const start = (cursor / total) * 100;
      cursor += slice.value;
      const end = (cursor / total) * 100;
      return `oklch(var(--${slice.token})) ${start}% ${end}%`;
    })
    .join(", ");

  return (
    <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
      <div className="relative flex h-40 w-40 shrink-0 items-center justify-center">
        {total > 0 ? (
          <div
            className="h-40 w-40 rounded-full"
            style={{ background: `conic-gradient(${stops})` }}
            aria-hidden="true"
          />
        ) : (
          <div className="h-40 w-40 rounded-full bg-background-100" aria-hidden="true" />
        )}
        <div className="absolute flex h-24 w-24 flex-col items-center justify-center rounded-full bg-background-50 px-2 text-center">
          <span className="font-heading text-base font-semibold leading-none text-foreground-950">
            {centerValue}
          </span>
          <span className="mt-1 text-[10px] uppercase tracking-wide text-foreground-500">{centerLabel}</span>
        </div>
      </div>

      <ul className="flex w-full flex-1 flex-col gap-3">
        {total === 0 && <li className="text-sm text-foreground-500">{emptyLabel}</li>}
        {total > 0 && slices.map((slice) => {
          const pct = total > 0 ? Math.round((slice.value / total) * 100) : 0;
          return (
            <li key={slice.key} className="flex items-center justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2.5">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: `oklch(var(--${slice.token}))` }}
                  aria-hidden="true"
                />
                <span className="truncate text-sm text-foreground-700">{slice.label}</span>
              </span>
              <span className="shrink-0 text-right">
                <span className="block text-sm font-label font-medium text-foreground-950">
                  {formatter(slice.value)}
                </span>
                <span className="block text-[11px] text-foreground-400">{pct} %</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}